import API from "../../utilities/LocalApiProxy";
import store from "../../store";
import getBulkUploadFailureMessage from "../../components/BulkUploadFailureMessage";
import { isPreventActionOnDeletedResource } from "../errorActions";
import { listDirectory } from "../documentActions";
import { openGlobalDialog } from "../globalEditDialogActions";
import {
	OPEN_REACT_DIALOG,
	MULTIPLE_FILE_UPLOAD_SUCCESS,
	MULTIPLE_FILE_UPLOAD_FAILURE,
	CLOSE_REACT_DIALOG,
	BULK_FILE_UPLOAD_FAILURE,
} from "../types";
import { FILE_UPLOAD_LIMITS } from "./constants";
import { prepareForUpload } from "./fileValidation";
import { countPDFFiles, checkOCRLimits } from "./ocrWarningHandler";
import {
	processFilesForUpload,
	chunkArray,
	mergeProfilingDataWithFiles,
} from "./fileProcessor";
import { handleDuplicateError } from "./duplicateHandler";
import { createDocumentAndFolderRecords } from "./s3Uploader";
import { getProfilingRequiredDialogMainDescription } from "../../components/mandatoryProfiling/dialog/profilingDialogUtils";

export async function asyncFileAndFolderMultipleUpload({
	matterID,
	basepath,
	directoryID,
	files,
	autoRename,
	callback,
	dispatch,
	userIgnoredTheLimit,
}) {
	prepareForUpload(dispatch);

	// Process files and prepare metadata
	const processedData = processFilesForUpload(files, matterID, basepath);
	let { topLevelFolders, topLevelFiles, filesToUpload, fileLocationInfo } =
		processedData;

	// Check OCR limits if needed
	const numPdfs = countPDFFiles(files);
	if (numPdfs > 0 && !userIgnoredTheLimit) {
		const shouldContinue = await checkOCRLimits({
			numPdfs,
			files,
			matterID,
			basepath,
			directoryID,
			autoRename,
			callback,
			dispatch,
		});
		if (!shouldContinue) {
			return;
		}
	}

	// Check for duplicates
	return API.post("/bulk/importDuplicationAndTrashCheck", {
		body: {
			topLevelFolders,
			topLevelFiles,
			directoryID,
			basepath,
			autoRename,
			filesToUpload, //sent for if files/folders need to be renamed
			fileLocationInfo, //sent for if folders need to be renamed
			matterID,
		},
	})
		.then((res) => {
			if (res?.mysql?.challenge === "PROFILING_REQUIRED") {
				// Open the mandatory profiling dialog
				const files = res.mysql.data.files || [];
				const availableOptions = res.mysql.data.availableOptions;
				const numFilesRequiringProfiling = files.filter(
					(file) =>
						file.requiredProfiling &&
						file.requiredProfiling.length > 0
				).length;

				const numFilesOriginallySelectedByUser = filesToUpload.length;

				let mainTextDescription =
					getProfilingRequiredDialogMainDescription({
						numFilesOriginallySelectedByUser,
						numFilesRequiringProfiling,
					});

				// Update filesToUpload with server-provided filenames (may be auto-renamed)
				filesToUpload = res.mysql.filesToUpload.map((file, index) => ({
					...file,
					lxwFilenameWithExtension: files[index].filename,
				}));

				return new Promise((resolve, reject) => {
					dispatch(
						openGlobalDialog(
							{
								type: "MandatoryProfiling",
								extraCSSClass: "LargeEditDialog",
								label:
									"Profile Document" +
									(numFilesRequiringProfiling > 1 ? "s" : ""),
								mainText: mainTextDescription,
								hideTextfield: true,
								submitButtonLabel: "Save All to LexWorkplace",
								cancelButtonLabel: "Cancel",
								bulkUploadProfilingSettings: {
									files,
									availableOptions,
								},
							},
							(state) => {
								// Process the profiling data and continue with file upload
								const profilingDataObject =
									state.mandatoryProfilingData;

								// Convert profiling data object to array format using server-provided filenames
								const profilingDataArray = files.map(
									(file, index) => ({
										filename: file.filename,
										...profilingDataObject[index],
									})
								);

								// Merge profiling data with filesToUpload
								filesToUpload = mergeProfilingDataWithFiles(
									filesToUpload,
									profilingDataArray
								);

								// Close the profiling dialog
								dispatch({
									type: CLOSE_REACT_DIALOG,
								});

								// Resolve with the updated data to continue the upload flow
								resolve(res);
							},
							(state) => {
								// Return validation state based on whether all required fields are filled
								return {
									canSubmit:
										state.mandatoryProfilingValid || false,
								};
							},
							() => {
								// User cancelled the profiling dialog
								reject("USER_CANCELLED");
							}
						)
					);
				});
			} else {
				return res;
			}
		})
		.then(async (res) => {
			// look for files response from the backend. should be transformed for at least the first request
			dispatch({
				type: OPEN_REACT_DIALOG,
				payload: {
					showSpinnerInTitle: true,
					title: "Uploading Files",
					mainContent: "Preparing Files for upload",
				},
			});

			// if autoRenamed get the response. Matches the format it was sent in
			if (autoRename && res.mysql.filesToUpload) {
				filesToUpload = res.mysql.filesToUpload;
			}
			const chunkSize = FILE_UPLOAD_LIMITS.CHUNK_SIZE;
			let chunkedFilesForFolderCreation = chunkArray(
				filesToUpload,
				chunkSize
			);
			for (let i = 0; i < chunkedFilesForFolderCreation.length; i++) {
				// reset fileIdMap every chunk to save on memory
				let fileIdMap = {};

				await createDocumentAndFolderRecords(
					chunkedFilesForFolderCreation[i],
					chunkSize,
					i,
					files,
					fileIdMap,
					dispatch
				);
			}
		})
		.then(() => {
			// list directory will call /document/list and it will transform from 0 to public/private then
			const bulkUploadFilesFailed =
				store.getState().document.bulkUploadFilesFailed;
			listDirectory(matterID, basepath)(dispatch);
			if (bulkUploadFilesFailed.length) {
				dispatch({
					type: BULK_FILE_UPLOAD_FAILURE,
					payload: {
						autoHideDuration: null,
						message: getBulkUploadFailureMessage(),
					},
				});
				dispatch({
					type: CLOSE_REACT_DIALOG,
				});
			} else {
				dispatch({
					type: MULTIPLE_FILE_UPLOAD_SUCCESS,
					payload: {
						num: files.length,
					},
				});
				dispatch({
					type: CLOSE_REACT_DIALOG,
				});
			}
			callback && callback();
		})
		.catch((err) => {
			const bulkUploadFilesFailed =
				store.getState().document.bulkUploadFilesFailed;
			if (bulkUploadFilesFailed.length) {
				dispatch({
					type: CLOSE_REACT_DIALOG,
				});
				dispatch({
					type: BULK_FILE_UPLOAD_FAILURE,
					payload: {
						autoHideDuration: null,
						message: getBulkUploadFailureMessage(),
					},
				});
			} else if (
				handleDuplicateError({
					err,
					matterID,
					basepath,
					directoryID,
					files,
					callback,
					dispatch,
					userIgnoredTheLimit,
				})
			) {
				// Handled by handleDuplicateError
			} else if (isPreventActionOnDeletedResource(err.lxwErrorCode)) {
				// handled in errorActions.js
			} else if (err === "USER_CANCELLED") {
				dispatch({
					type: CLOSE_REACT_DIALOG,
				});
			} else {
				// upload probably failed for unknown reason
				dispatch({
					type: MULTIPLE_FILE_UPLOAD_FAILURE,
					payload: { num: files.length },
				});
				dispatch({
					type: CLOSE_REACT_DIALOG,
				});
				console.log("generic error catch, or all files failed", err);
			}
		});
}
